import type { ComponentType } from "react";
import type { RouteObject } from "react-router";
import type { ApiArea } from "@/api/areas";
import i18n, { SCREEN_NAMESPACES } from "@/i18n";

/**
 * What a feature exports from `src/features/<area>/routes.tsx`. The router picks up every such file, so
 * adding a screen never touches a shared file.
 *  - `public`: reachable signed out (sign-in, the design reference).
 *  - `shell`: inside the app shell (tab bar on phones, side rail on desktop), signed in.
 *  - `fullscreen`: signed in, no navigation chrome (capture, reveal).
 * Paths are relative to the root ("hoardex", "cards/:cardId"); use `page()` so each screen is its own chunk.
 */
export interface FeatureRoutes {
  public?: RouteObject[];
  shell?: RouteObject[];
  fullscreen?: RouteObject[];
}

/** A lazily loaded screen: the module's default export becomes the route's component. The strings every
 * screen shares (SCREEN_NAMESPACES) load alongside it, not after it renders. */
export const page = (load: () => Promise<{ default: ComponentType }>) => async () => {
  const [module] = await Promise.all([load(), i18n.loadNamespaces([...SCREEN_NAMESPACES])]);
  return { Component: module.default };
};

/** A route's `handle`: the API areas its screen shows. A real build marks the ones still answered by the mock
 * as demo data (src/app/DemoNotice.tsx). */
export interface AreasHandle {
  areas: readonly ApiArea[];
}

export const shows = (...areas: ApiArea[]): AreasHandle => ({ areas });
